import {
  makeDataState,
  makePaginationState,
  filterUnique,
  concatUnique,
  STATUSES,
} from 'app/utils/redux';

import * as t from './types';

const initialState = {
  communityPost: makeDataState(),
  communityPostComments: makePaginationState(),
  communityPostFixedComments: makePaginationState(),
  communityPostBestComments: makePaginationState(),
};

const _protectCachedLikeCount = (items) =>
  items.map((item) => {
    if (item.isLiked && item.likeCount === 0) {
      return {
        ...item,
        likeCount: 1,
      };
    }

    return item;
  });

const _likeCommentIterator = (item, commentId, isAuthor) => {
  if (item.id === commentId) {
    return {
      ...item,
      isLiked: !item.isLiked,
      likeCount: item.isLiked ? item.likeCount - 1 : item.likeCount + 1,
      ...(isAuthor ? { isObjectOwnerLiked: !item.isLiked } : {}),
    };
  }

  return item;
};

export default (state = initialState, action) => {
  switch (action.type) {
    // fetch communityPost
    case `${t.FETCH_COMMUNITY_POST}_PENDING`:
      return {
        ...state,
        communityPost: {
          ...state.communityPost,
          status: STATUSES.PENDING,
        },
      };

    case `${t.FETCH_COMMUNITY_POST}_SUCCESS`:
      return {
        ...state,
        communityPost: {
          ...state.communityPost,
          status: STATUSES.SUCCESS,
          isLoaded: true,
          data: action.payload.result,
        },
      };

    case `${t.FETCH_COMMUNITY_POST}_ERROR`:
      return {
        ...state,
        communityPost: {
          ...state.communityPost,
          status: STATUSES.ERROR,
        },
      };

    // like communityPost
    case `${t.LIKE_COMMUNITY_POST}_SUCCESS`: {
      return {
        ...state,
        communityPost: {
          ...state.communityPost,
          data: {
            ...state.communityPost.data,
            isLiked: !state.communityPost.data.isLiked,
            likeCount: state.communityPost.data.isLiked
              ? state.communityPost.data.likeCount - 1
              : state.communityPost.data.likeCount + 1,
          },
        },
      };
    }

    // fetch community fixed comments
    case `${t.FETCH_COMMUNITY_POST_FIXED_COMMENTS}_PENDING`:
      return {
        ...state,
        communityPostFixedComments: {
          ...state.communityPostFixedComments,
          status: STATUSES.PENDING,
        },
      };
    case `${t.FETCH_COMMUNITY_POST_FIXED_COMMENTS}_SUCCESS`:
      return {
        ...state,
        communityPostFixedComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...state.communityPostFixedComments.pagination,
            items: _protectCachedLikeCount(action.payload.result.items),
          },
        },
      };
    case `${t.FETCH_COMMUNITY_POST_FIXED_COMMENTS}_ERROR`:
      return {
        ...state,
        communityPostFixedComments: {
          ...state.communityPostFixedComments,
          status: STATUSES.ERROR,
        },
      };

    // fetch community best comments
    case `${t.FETCH_COMMUNITY_POST_BEST_COMMENTS}_PENDING`:
      return {
        ...state,
        communityPostBestComments: {
          ...state.communityPostBestComments,
          status: STATUSES.PENDING,
        },
      };
    case `${t.FETCH_COMMUNITY_POST_BEST_COMMENTS}_SUCCESS`:
      return {
        ...state,
        communityPostBestComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...state.communityPostBestComments.pagination,
            items: filterUnique(
              _protectCachedLikeCount(action.payload.result.items),
              state.communityPostFixedComments.pagination.items
            ),
          },
        },
      };
    case `${t.FETCH_COMMUNITY_POST_BEST_COMMENTS}_ERROR`:
      return {
        ...state,
        communityPostBestComments: {
          ...state.communityPostBestComments,
          status: STATUSES.ERROR,
        },
      };

    // fetch community comments
    case `${t.FETCH_COMMUNITY_POST_COMMENTS}_PENDING`:
      return {
        ...state,
        communityPostComments: {
          ...state.communityPostComments,
          status: STATUSES.PENDING,
        },
      };

    case `${t.FETCH_COMMUNITY_POST_COMMENTS}_SUCCESS`:
      return {
        ...state,
        communityPostComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...action.payload.result,
            items: concatUnique(
              state.communityPostComments.pagination.items,
              filterUnique(
                _protectCachedLikeCount(action.payload.result.items),
                state.communityPostBestComments.pagination.items
              )
            ),
          },
        },
      };

    case `${t.FETCH_COMMUNITY_POST_COMMENTS}_ERROR`:
      return {
        ...state,
        communityPostComments: {
          ...state.communityPostComments,
          status: STATUSES.ERROR,
        },
      };

    // post communityPost comments
    case `${t.POST_COMMUNITY_POST_COMMENT}_PENDING`:
      return {
        ...state,
        communityPostComments: {
          ...state.communityPostComments,
          status: STATUSES.PENDING,
        },
      };

    case `${t.POST_COMMUNITY_POST_COMMENT}_SUCCESS`:
      return {
        ...state,
        communityPostComments: {
          status: STATUSES.SUCCESS,
          isLoaded: true,
          pagination: {
            ...state.communityPostComments.pagination,
            totalCount: state.communityPostComments.pagination.totalCount + 1,
            items: [action.payload.result].concat(
              _protectCachedLikeCount(
                state.communityPostComments.pagination.items
              )
            ),
          },
        },
        communityPost: {
          ...state.communityPost,
          data: {
            ...state.communityPost.data,
            commentCount: state.communityPost.data.commentCount + 1,
          },
        },
      };

    case `${t.POST_COMMUNITY_POST_COMMENT}_ERROR`:
      return {
        ...state,
        communityPostComments: {
          ...state.communityPostComments,
          status: STATUSES.ERROR,
        },
      };

    // like comment
    case `${t.LIKE_COMMENT}_SUCCESS`: {
      const { userId, commentId } = action.meta;
      const isAuthor = userId === state.communityPost.data.user.id;

      const _likeComment = (commentType) => ({
        [commentType]: {
          ...state[commentType],
          pagination: {
            ...state[commentType].pagination,
            items: state[commentType].pagination.items.map((item) =>
              _likeCommentIterator(item, commentId, isAuthor)
            ),
          },
        },
      });

      return {
        ...state,
        ..._likeComment('communityPostFixedComments'),
        ..._likeComment('communityPostBestComments'),
        ..._likeComment('communityPostComments'),
      };
    }

    // delete or delete comment
    case `${t.DELETE_COMMENT}_SUCCESS`:
    case `${t.HIDE_COMMENT}_SUCCESS`: {
      const { commentId } = action.meta;

      const _removeComment = (commentType) => ({
        [commentType]: {
          ...state[commentType],
          pagination: {
            ...state[commentType].pagination,
            items: state[commentType].pagination.items.filter(
              (item) => item.id !== commentId
            ),
          },
        },
      });

      return {
        ...state,
        ..._removeComment('communityPostFixedComments'),
        ..._removeComment('communityPostBestComments'),
        ..._removeComment('communityPostComments'),
      };
    }

    case t.RESET_COMMUNITY_POST: {
      return {
        ...state,
        communityPost: makeDataState(),
        communityPostFixedComments: makePaginationState(),
        communityPostBestComments: makePaginationState(),
        communityPostComments: makePaginationState(),
      };
    }

    case t.RESET_COMMENTS: {
      return {
        ...state,
        communityPostFixedComments: makePaginationState(),
        communityPostBestComments: makePaginationState(),
        communityPostComments: makePaginationState(),
      };
    }

    default:
      return {
        ...state,
      };
  }
};



// WEBPACK FOOTER //
// ./src/app/redux/front/communityPostDetail/reducer.js